import { useEffect, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { GetSubmissionDashboardResponse } from '@ebalance/proto/bffuser/v1/submission_management_service_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { Box, TertiaryButton } from '@enechain/ecloud-designsystem'
import { FaGearRegular } from '@enechain/ecloud-icons'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { CompactAlert } from '~/components/common/CompactAlert'
import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import { PageBody } from '~/components/common/PageBody'
import { PlaceholderPage } from '~/components/common/PlaceholderPage'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import {
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import { useServerTimeOffsetMs } from '~/hooks/useServerTimeOffsetMs'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { now as serverNow, serverTimeOffsetMs } from '~/models/clock'
import { PlanTypeCardGrid } from '~/pages/SubmissionManagement/components/PlanTypeCardGrid'
import {
  AreaTabIndicators,
  AttentionMarksSummary,
  type DayIndicatorCounts,
  DayTabIndicators,
} from '~/pages/SubmissionManagement/components/TabIndicators'
import { useSubmissionDashboardQuery } from '~/pages/SubmissionManagement/hooks/useSubmissionDashboardQuery'
import { areaTabAvailability } from '~/pages/SubmissionManagement/models/areaTab'
import {
  SUBMISSION_STATUS_FILTER_OPTIONS,
  type SubmissionStatusFilterOption,
  countOfOption,
} from '~/pages/SubmissionManagement/models/submissionStatusFilter'
import {
  TARGET_DAY_SEGMENTS,
  type TargetDaySegment,
  formatTargetDateLabel,
  resolveTargetDate,
} from '~/pages/SubmissionManagement/models/targetDaySegment'
import { routePaths } from '~/routes/routePaths'

/**
 * 提出管理ダッシュボード (`docs/specs/plan-submit/submission-management.md`、
 * Design Note `submission-management-dashboard-skeleton`)。
 *
 * flag OFF (評価不能含む) の組織には既存 `PlaceholderPage` を出し、集約 RPC は
 * 業務データを返さない (Decision 3/9)。初回未解決 (RPC or 業務エリア設定) は
 * プレースホルダもダッシュボードも出さない (Decision 9/17)。**初回解決後**は
 * ページ外枠 (breadcrumb / WorkflowBackBar / エリア・日断面タブ) を常駐させ、
 * 日断面切替中の読込 / エラーは grid の位置だけで切り替える (review 指摘: query key
 * が変わるたびにタブごと unmount すると focus が失われる)。業務エリア設定の
 * 取得失敗は、一度も取得できないままなら読込エラーにする (AC-PSM-075。
 * Decision 19 の fail-open を上書き。共有 hook の fail-open 自体は他画面のため不変)。
 */
export const SubmissionManagementPage = (): React.ReactElement => {
  const { t, i18n } = useTranslation()

  // AC-PSM-030〜032: caseId 付きで開いている間の WorkflowBackBar は
  // WorkflowPageLayout が出す。
  // isReadonly は本 slice に変更系操作が無いため参照しない (Decision 13。
  // カード遷移は読み取り専用でも常に有効)。後続 slice (7/9) が isReadonly で
  // 変更系操作を無効化する時に、ここから引き継ぐ。
  const { isWorkflowNotFound } = useCurrentWorkflowCase()

  // 日断面タブ (AC-PSM-002/005)。caseId があっても対象日はロックしない (Decision 11)。
  // AC-PSM-002: 開いたまま 0:00 をまたいでも再読込までは前の日のまま揃える。
  // offset は TickerBar の session 再取得 (5 分間隔) のたびに通信遅延ぶん微修正
  // されるので、対象日の基準時刻は「開いた時点のブラウザ時計 + 最新の offset」に
  // 固定し、offset の微修正では日を進めない。
  const [daySegment, setDaySegment] = useState<TargetDaySegment>('today')
  const offsetMs = useServerTimeOffsetMs()
  const [openedAtBrowserMs] = useState(
    () => serverNow().getTime() - serverTimeOffsetMs(),
  )
  // 当日・翌日は日断面ごとの 2 query として並行取得する (Design Note
  // `submission-tab-indicators` Decision 1/8)。grid は表示中日断面の応答、タブの
  // インジケータは各日の応答だけから作る。
  const [targetDates, setTargetDates] = useState(() => {
    const openedAt = new Date(openedAtBrowserMs + serverTimeOffsetMs())
    return {
      today: resolveTargetDate('today', openedAt),
      tomorrow: resolveTargetDate('tomorrow', openedAt),
    }
  })
  useEffect(() => {
    const openedAt = new Date(openedAtBrowserMs + offsetMs)
    setTargetDates({
      today: resolveTargetDate('today', openedAt),
      tomorrow: resolveTargetDate('tomorrow', openedAt),
    })
  }, [openedAtBrowserMs, offsetMs])
  const todayQuery = useSubmissionDashboardQuery(targetDates.today)
  const tomorrowQuery = useSubmissionDashboardQuery(targetDates.tomorrow)
  const queryBySegment: Record<TargetDaySegment, typeof todayQuery> = {
    today: todayQuery,
    tomorrow: tomorrowQuery,
  }
  const activeQuery = queryBySegment[daySegment]
  const otherQuery =
    queryBySegment[daySegment === 'today' ? 'tomorrow' : 'today']
  // 日断面タブの切替は表示する query を選び直すだけで key は変わらない (両日の
  // query は常駐)。表示中日断面の応答が未着の間 (もう一方より遅れて取得中) は、
  // `activeQuery.data ?? otherQuery.data` でもう一方の応答からページ外枠 (タブ) を
  // 保ち、grid だけローディングにして focus を保つ。どちらかの日が応答未取得の
  // まま失敗した場合は、下の `isLoadFailed` で読込エラーにする。
  const data: GetSubmissionDashboardResponse | undefined =
    activeQuery.data ?? otherQuery.data
  // `keepPreviousData` により、対象日の key が変わった直後 (offset 確定・日付変更)
  // は前 key の応答が placeholder として `data` に残る (残さないと data が
  // undefined になりタブごと unmount して focus を失う)。placeholder の間は
  // grid をローディングにする。
  const isGridLoading =
    activeQuery.data === undefined || activeQuery.isPlaceholderData

  // 選べるエリア = 応答エリア (OCCTO マスタ登録あり) ∩ 業務エリア設定 (AC-PSM-001、
  // Established Contract `useBusinessAreaFilter`)。タブには対象 10 エリアを全て並べ、
  // それ以外は押せないタブとして残す。`isBusinessAreasResolved` (成功 or 恒久的な失敗) が
  // 確定するまではタブ・初期選択を確定しない (fail-open の暫定集合が一瞬でも
  // 選択肢として確定することを防ぐ、Decision 10)。
  const {
    selectableAreaCodes: businessAreaCodes,
    isBusinessAreasResolved,
    isBusinessAreasLoadFailed,
  } = useBusinessAreaFilter(AREA_CODE_OPTIONS)
  // AC-PSM-075: 当日・翌日どちらかの集約 RPC が応答を一度も得ないまま失敗した時、
  // または業務エリア設定を一度も取得できないまま失敗した時は読込エラーにする
  // (エリアタブの印は当日∪翌日から作るため、片方の日が黙って欠けない)。取得済み
  // の後の背景再取得失敗は data が残るので表示を保つ。業務エリア設定の失敗は
  // 集約 RPC の応答 (data) が届いてから判定する: flag OFF の組織にはプレース
  // ホルダだけを出し、読込エラーを経由させないため (下の flag OFF 分岐が先)。
  const isLoadFailed =
    (isBusinessAreasLoadFailed && data !== undefined) ||
    [todayQuery, tomorrowQuery].some(
      (query) => query.isError && query.data === undefined,
    )

  const responseAreaCodes = useMemo(
    () => new Set((data?.areas ?? []).map((area) => area.areaCode)),
    [data],
  )
  // AREA_CODE_OPTIONS 順 (Decision 10)。
  const areaTabs = useMemo(
    () =>
      AREA_CODE_OPTIONS.map((code) => ({
        code,
        availability: areaTabAvailability(
          code,
          responseAreaCodes,
          businessAreaCodes,
        ),
      })),
    [responseAreaCodes, businessAreaCodes],
  )
  const selectableTabAreaCodes = useMemo(
    () =>
      areaTabs
        .filter(({ availability }) => availability === 'selectable')
        .map(({ code }) => code),
    [areaTabs],
  )

  // ユーザーが明示的にクリックしたエリア (未クリックなら undefined = 既定の
  // 先頭選択、Decision 10)。
  const [manualAreaCode, setManualAreaCode] = useState<AreaCode | undefined>(
    undefined,
  )
  const currentAreaCode =
    manualAreaCode !== undefined &&
    selectableTabAreaCodes.includes(manualAreaCode)
      ? manualAreaCode
      : selectableTabAreaCodes[0]

  // 提出ステータスの絞込 (AC-PSM-070、Decision 8)。ページ内 state のみ (URL に載せない)。
  // エリア・日断面を切り替えても保つ。
  const [statusFilter, setStatusFilter] =
    useState<SubmissionStatusFilterOption>('all')

  const breadcrumbItems = [
    { label: t('common:nav.plan') },
    { label: t(routePaths.submissionManagement.name), bold: true },
  ]

  // flag OFF の応答があればプレースホルダを優先する (評価不能含む、Decision 3/9)。
  // ただし AC-WFW-040 は feature flag による例外を設けないので、ワークフローが見つからない
  // 時はプレースホルダを出さず、下の WorkflowPageLayout の not-found 表示に流す
  // (PlaceholderPage は外枠ごと差し替える全画面で、WorkflowPageLayout を通らないため)。
  if (data?.featureEnabled === false && !isWorkflowNotFound) {
    return <PlaceholderPage routeKey="submissionManagement" />
  }

  // 読込エラーは 1 経路 (AC-PSM-075): 応答未取得のまま失敗した日がある / 業務
  // エリア設定を一度も取得できないまま失敗した、のいずれも本分岐でエラー表示にし、
  // プレースホルダにもダッシュボードにもしない。取得済みの応答があれば背景再取得の
  // 失敗では表示を保つ (`ProcurementPlanList` / `BalanceDemand` と同じ形)。
  // 応答が無く失敗もしていない間 (初回取得中) はローディング。
  if (isLoadFailed || data === undefined) {
    return (
      <WorkflowPageLayout breadcrumbItems={breadcrumbItems}>
        {isLoadFailed ? (
          <CompactAlert
            status="error"
            title={t('page:submissionManagement.loadError')}
          />
        ) : (
          <RegionLoading minHeight="200px" isLoading />
        )}
      </WorkflowPageLayout>
    )
  }

  // ここから先はページ外枠 (breadcrumb / WorkflowBackBar) を 1 つに統一し、
  // 状態に応じて body だけを選ぶ (review 指摘)。
  let body: React.ReactNode
  if (!isBusinessAreasResolved) {
    // AC-PSM-001: RPC は解決済みでも業務エリア設定が未解決の間はタブ (初期選択
    // 含む) を確定させない。
    body = <RegionLoading minHeight="200px" isLoading />
  } else if (selectableTabAreaCodes.length === 0) {
    // AC-PSM-052: 選べるエリアタブ 0 本 → EmptyPanel + 個人設定への導線のみ。
    // 押せないタブも含めてタブ・grid は描画しない。
    body = (
      <EmptyPanel
        action={
          <TertiaryButton asChild>
            <RouterLink to={routePaths.settings.path}>
              {t('page:submissionManagement.emptyPanel.toSettings')}
            </RouterLink>
          </TertiaryButton>
        }
        description={t('page:submissionManagement.emptyPanel.description')}
        icon={
          <FaGearRegular boxSize={EMPTY_PANEL_ICON_SIZE} aria-hidden="true" />
        }
        title={t('page:submissionManagement.emptyPanel.title')}
      />
    )
  } else {
    const indicatorOf = (
      query: typeof todayQuery,
      areaCode: AreaCode | undefined,
    ): DayIndicatorCounts | undefined => {
      // 取得中 (placeholder 含む) / 取得失敗の日は不明として出さない (Decision 8)。
      if (
        query.data === undefined ||
        query.isPlaceholderData ||
        query.isError
      ) {
        return undefined
      }
      return query.data.areas.find((area) => area.areaCode === areaCode)
        ?.tabIndicator
    }
    const selectedArea = data.areas.find(
      (area) => area.areaCode === currentAreaCode,
    )
    const selectedAreaCards = selectedArea?.cards ?? []

    // 件数は表示中エリア × 日断面の応答の写像。取得中 (placeholder 含む) の日は出さない。
    const filterCounts = isGridLoading
      ? undefined
      : selectedArea?.submissionStatusFilterCounts

    const areaFilterLabel = t('page:submissionManagement.areaLabel')
    const dayFilterLabel = t('page:submissionManagement.dayLabel')
    const statusFilterLabel = t('page:submissionManagement.statusFilter.label')
    const dayLabelOf = (segment: TargetDaySegment): string =>
      t(`page:submissionManagement.daySegment.${segment}`, {
        date: formatTargetDateLabel(
          targetDates[segment],
          i18n.language.startsWith('en'),
        ),
      })
    // 開閉式フィルター (UI 辞書 toolbar-collapsible.md「提出管理」)。 閉じている間は今の値を要約で読む。
    // 件数・クリアは提出ステータスだけ。 エリアは既定が無く、 日断面は日付なので数えず戻さない。
    const filterSummary: ToolbarStatusItem[] = [
      {
        key: 'area',
        label: areaFilterLabel,
        value:
          currentAreaCode === undefined
            ? ''
            : convertAreaLabel(currentAreaCode),
      },
      { key: 'day', label: dayFilterLabel, value: dayLabelOf(daySegment) },
      {
        key: 'status',
        label: statusFilterLabel,
        value: t(`page:submissionManagement.statusFilter.${statusFilter}`),
      },
    ]
    /*
     * カードの中の注意の印 (エリア × 当日・翌日の 赤 = 取引相手間ミスマッチ・差戻し / ▲ = 計画内不整合) の合計。
     * 印がある間は閉じていても開いて見せ (hasAttention)、閉じている間はツールバーに合計を出す (collapsedStatus)。
     * 未提出はふだんから出る件数なので数えない。押せないエリアは印を付けないので数えない (AC-PSM-013)
     */
    const attentionTotals = areaTabs
      .filter(({ availability }) => availability === 'selectable')
      .reduce(
        (totals, { code }) => {
          for (const query of [todayQuery, tomorrowQuery]) {
            const counts = indicatorOf(query, code)
            if (counts === undefined) continue
            totals.mismatch += counts.counterpartyMismatchOrRejectedPlanCount
            totals.internal += counts.internalInconsistencyPlanCount
          }
          return totals
        },
        { mismatch: 0, internal: 0 },
      )
    const filterItems: FilterGridItem[] = [
      {
        key: 'area',
        label: areaFilterLabel,
        control: (
          <AccessibleSegmentedControl
            aria-label={areaFilterLabel}
            name="submission-management-area"
            onSelect={(value) => setManualAreaCode(Number(value) as AreaCode)}
            options={areaTabs.map(({ code, availability }) => ({
              value: String(code),
              // 押せないタブは DS の option 単位 disabled (native radio の
              // disabled) で、押しても選択は変わらない。理由は入札の発注主体区分
              // (`issuerAreaOptionLabel`) と同じ「エリア名 (理由)」の形で添え、
              // そのエリアのデータを表示・集計しないためインジケータは付けない
              // (AC-PSM-001 / 013)。
              disabled: availability !== 'selectable',
              label:
                availability === 'selectable' ? (
                  <>
                    {convertAreaLabel(code)}
                    <AreaTabIndicators
                      today={indicatorOf(todayQuery, code)}
                      tomorrow={indicatorOf(tomorrowQuery, code)}
                    />
                  </>
                ) : (
                  `${convertAreaLabel(code)} (${t(
                    `page:submissionManagement.areaUnavailable.${availability}`,
                  )})`
                ),
            }))}
            selected={String(currentAreaCode)}
          />
        ),
      },
      {
        key: 'day',
        label: dayFilterLabel,
        control: (
          <AccessibleSegmentedControl
            aria-label={dayFilterLabel}
            name="submission-management-day"
            onSelect={(segment) => setDaySegment(segment)}
            options={TARGET_DAY_SEGMENTS.map((segment) => ({
              value: segment,
              label: (
                <>
                  {/* 対象日は集約 RPC の request に載せた日付そのもの。grid は
                        その日の応答が placeholder でない時だけカードを出すので、
                        タブの日付と表示中のカードの対象日は食い違わない
                        (AC-PSM-002)。 */}
                  {t(`page:submissionManagement.daySegment.${segment}`, {
                    date: formatTargetDateLabel(
                      targetDates[segment],
                      i18n.language.startsWith('en'),
                    ),
                  })}
                  <DayTabIndicators
                    counts={indicatorOf(
                      queryBySegment[segment],
                      currentAreaCode,
                    )}
                  />
                </>
              ),
            }))}
            selected={daySegment}
          />
        ),
      },
      {
        key: 'status',
        label: statusFilterLabel,
        control: (
          <AccessibleSegmentedControl
            aria-label={statusFilterLabel}
            name="submission-management-status-filter"
            onSelect={(option) => setStatusFilter(option)}
            options={SUBMISSION_STATUS_FILTER_OPTIONS.map((option) => {
              const count = countOfOption(filterCounts, option)
              return {
                value: option,
                label: (
                  <>
                    {t(`page:submissionManagement.statusFilter.${option}`)}
                    {count !== undefined && (
                      <Box as="span" marginLeft={1.5}>
                        {count}
                      </Box>
                    )}
                  </>
                ),
              }
            })}
            selected={statusFilter}
          />
        ),
      },
    ]

    body = (
      <>
        <CollapsibleFilterToolbar
          collapsedStatus={<AttentionMarksSummary {...attentionTotals} />}
          hasAttention={attentionTotals.mismatch + attentionTotals.internal > 0}
          modifiedCount={statusFilter === 'all' ? 0 : 1}
          onClear={() => setStatusFilter('all')}
          storageKey="submissionManagement"
          summary={filterSummary}
        >
          <FilterGrid items={filterItems} />
        </CollapsibleFilterToolbar>
        {/* 表示中日断面の応答が未着 / placeholder (offset 確定・日付変更で key が
            変わった直後) の間だけ grid をローディングにし、ツールバーは常駐
            させたまま focus を保つ。 */}
        <RegionLoading minHeight="200px" isLoading={isGridLoading}>
          {isGridLoading ? null : (
            // 絞込・列の組み方・空判定は PlanTypeCardGrid の 1 箇所。
            <PlanTypeCardGrid
              cards={selectedAreaCards}
              statusFilter={statusFilter}
              targetDate={targetDates[daySegment]}
            />
          )}
        </RegionLoading>
      </>
    )
  }

  return (
    <WorkflowPageLayout breadcrumbItems={breadcrumbItems}>
      <PageBody>{body}</PageBody>
    </WorkflowPageLayout>
  )
}
